import { useEffect, useState } from 'react';
import { Badge, Banner, BlockStack, Button, Checkbox, Divider, InlineStack, Link, Select, Text, TextField } from '@shopify/polaris';
import axios from 'axios';
import agent from '../../app/api/agent';
import { AdminStoreReview, VehicleSearchReviewResult } from '../../app/models/Admin';
import { formatLocalDateTime } from './utils/dateTime';
import { vehicleSearchReviewLabel, vehicleSearchReviewOptions } from './utils/storeReview';

type Props = { id: number; legacySource?: boolean; token: string; onUnauthorized: () => void };

export default function StoreReviewPanel({ id, legacySource = false, token, onUnauthorized }: Props) {
  const [saved, setSaved] = useState<AdminStoreReview>();
  const [draft, setDraft] = useState<AdminStoreReview>();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);
  useEffect(() => {
    let current = true;
    setSaved(undefined); setDraft(undefined); setError(undefined); setSuccess(false);
    agent.Admin.storeReview(token, id, legacySource).then(value => {
      if (current) { setSaved(value); setDraft(value); }
    }).catch(e => {
      if (!current) return;
      if (axios.isAxiosError(e) && e.response?.status === 401) onUnauthorized();
      else setError('Unable to load store notes and flags.');
    });
    return () => { current = false; };
  }, [id, legacySource, token, onUnauthorized]);
  const change = <K extends keyof AdminStoreReview>(key: K, value: AdminStoreReview[K]) => {
    setDraft(d => d ? { ...d, [key]: value } : d); setSuccess(false);
  };
  const save = async () => {
    if (!draft) return;
    setBusy(true); setError(undefined); setSuccess(false);
    try {
      const result = await agent.Admin.saveStoreReview(token, id, legacySource, draft);
      setSaved(result); setDraft(result); setSuccess(true);
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 401) onUnauthorized();
      else setError(axios.isAxiosError(e) && typeof e.response?.data?.message === 'string' ? e.response.data.message : 'Unable to save store details. Your changes are still here.');
    } finally { setBusy(false); }
  };
  const urls = (saved?.vehicleSearchPageUrls || '').split(/\r?\n/).filter(Boolean);
  const dirty = JSON.stringify(saved) !== JSON.stringify(draft);
  return <BlockStack gap="400">
    {error ? <Banner tone="critical">{error}</Banner> : null}
    {!draft && !error ? <Text as="p" tone="subdued">Loading notes and flags…</Text> : null}
    {draft ? <>
      <BlockStack gap="200">
        <Text as="h3" variant="headingMd">Migration flags</Text>
        <BlockStack gap="100">
          {(saved?.vehicleCollectionMappingRows ?? 0) > 0 && <div><Badge>Vehicle/collection mapping</Badge></div>}
          <Text as="p">PFV1 vehicle/collection mappings: {saved?.vehicleCollectionMappingRows == null
            ? 'Unknown — no verified PFV1 snapshot.' : `${saved.vehicleCollectionMappingRows.toLocaleString()} mapping records`}</Text>
          {saved?.vehicleCollectionMappingCheckedAt && <Text as="p" tone="subdued">Detected automatically from PFV1 data · Checked {formatLocalDateTime(saved.vehicleCollectionMappingCheckedAt)}. Records do not confirm current storefront use.</Text>}
        </BlockStack>
        <Checkbox label="Needs attention" checked={draft.needsAttention} onChange={v => change('needsAttention', v)} disabled={busy} />
        <Checkbox label="Storefront review blocked" checked={draft.storefrontReviewBlocked} onChange={v => change('storefrontReviewBlocked', v)} disabled={busy}
          helpText="Password protection or another access blocker. Record the reason in Notes." />
        <Checkbox label="Other fitment/search provider detected" checked={draft.otherFitmentProviderDetected} onChange={v => change('otherFitmentProviderDetected', v)} disabled={busy}
          helpText="Record the provider and where it was found in Notes." />
        <Checkbox label="Pre-existing app issues" checked={draft.hasPreExistingAppIssues} onChange={v => change('hasPreExistingAppIssues', v)} disabled={busy}
          helpText="Issues observed before migration. Record details in Notes." />
        <Checkbox label="Uses custom category/collection rendering" checked={draft.usesCustomCategoryRendering} onChange={v => change('usesCustomCategoryRendering', v)} disabled={busy} />
        <Checkbox label="Uses merchant-defined rendering code" checked={draft.usesMerchantRenderingCode} onChange={v => change('usesMerchantRenderingCode', v)} disabled={busy} />
      </BlockStack>
      <TextField label="Notes" multiline={5} value={draft.notes} onChange={v => change('notes', v)} autoComplete="off" disabled={busy} />
      <Divider />
      <BlockStack gap="200">
        <InlineStack align="space-between"><Text as="h3" variant="headingMd">Vehicle search pages</Text>
          <Text as="span" tone="subdued">{vehicleSearchReviewLabel(saved?.vehicleSearchReviewResult)}{saved?.vehicleSearchReviewedAt ? ` · ${formatLocalDateTime(saved.vehicleSearchReviewedAt)}` : ''}</Text></InlineStack>
        <Select label="Vehicle search review result" value={draft.vehicleSearchReviewResult} options={vehicleSearchReviewOptions}
          onChange={v => change('vehicleSearchReviewResult', v as VehicleSearchReviewResult)} disabled={busy}
          helpText="Result of the checked pages and sample search. Keep test details and limitations in Notes." />
        {urls.length ? urls.map(url => /^https?:\/\//i.test(url)
          ? <div key={url} style={{ overflowWrap: 'anywhere' }}><Link url={url} target="_blank">{url}</Link></div>
          : <Text as="p" key={url}>{url}</Text>) : <Text as="p" tone="subdued">No page URLs recorded.</Text>}
        <details><summary style={{ cursor: 'pointer', width: 'fit-content' }}>Edit page URLs</summary>
          <div style={{ paddingTop: 12 }}><BlockStack gap="300">
            <TextField label="Page URLs" helpText="One complete URL per line." multiline={4} value={draft.vehicleSearchPageUrls}
              onChange={v => change('vehicleSearchPageUrls', v)} autoComplete="off" disabled={busy} />
            <InlineStack gap="200"><Button disabled={busy} onClick={() => change('vehicleSearchReviewedAt', new Date().toISOString())}>Mark reviewed today</Button>
              {draft.vehicleSearchReviewedAt ? <Button variant="plain" disabled={busy} onClick={() => change('vehicleSearchReviewedAt', null)}>Clear review date</Button> : null}
              {draft.vehicleSearchReviewedAt !== saved?.vehicleSearchReviewedAt ? <Text as="span">Review date: {formatLocalDateTime(draft.vehicleSearchReviewedAt || undefined)}</Text> : null}</InlineStack>
          </BlockStack></div>
        </details>
      </BlockStack>
      <InlineStack gap="300" blockAlign="center">
        <Button variant="primary" loading={busy} disabled={!dirty || busy} onClick={() => void save()}>Save store details</Button>
        {dirty ? <Button disabled={busy} onClick={() => { setDraft(saved); setSuccess(false); setError(undefined); }}>Discard changes</Button> : null}
        {success ? <Text as="span" tone="success">Saved</Text> : dirty ? <Text as="span" tone="subdued">Unsaved changes</Text> : null}
      </InlineStack>
    </> : null}
  </BlockStack>;
}
